import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { User, CreateUserRequest, ApiResponse, Farm, CreateFarmRequest, SelfBenefit, CreateSelfBenefitRequest, ReferralMilestone, CreateReferralMilestoneRequest, ReferralConfig, UpdateReferralConfigRequest, RoleChangeRequest, RoleChangeRequestResponse, AssignAdminToFarmRequest, Market, CreateMarketRequest } from '../types';

/*
const AES_KEY = process.env.REACT_APP_PAYLOAD_AES_KEY || '';

// Backend Decryption AES Key loading
if (!AES_KEY) {
  // Silent warning for production unless critical
}


function wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const u8 = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return u8;
}

function decrypt(encryptedBase64: string) {
  if (!AES_KEY) {
    return null;
  }
  try {
    const raw = CryptoJS.enc.Base64.parse(encryptedBase64);
    const iv = CryptoJS.lib.WordArray.create(raw.words.slice(0, 4), 16);
    const ciphertext = CryptoJS.lib.WordArray.create(raw.words.slice(4), raw.sigBytes - 16);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext } as any,
      CryptoJS.enc.Utf8.parse(AES_KEY),
      { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    // Backend always gzip-compresses before encrypting — decompress first
    const decompressed = pako.ungzip(wordArrayToUint8Array(decrypted));
    const jsonStr = new TextDecoder().decode(decompressed);

    if (!jsonStr) {
      return null;
    }
    return JSON.parse(jsonStr);
  } catch (error) {
    return null;
  }
}
*/

export const api = axios.create({
  timeout: 30000,
});

// Axios interceptor — auto-decrypts every response
/*
api.interceptors.response.use(
  (response) => {
    if (response.data?.payload) {
      const decryptedData = decrypt(response.data.payload);
      if (decryptedData) {
        response.data = decryptedData;
      }
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);
*/


// Helper to extract a string error message from various backend response formats
const extractErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  if (typeof error === 'string') return error;

  // If it's already an error object we extracted
  if (error.error && typeof error.error === 'string') return error.error;

  // Handle Axios error structure or direct response data
  const data = error?.response?.data || error;

  if (data) {
    if (data.message) return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
    if (data.detail) {
      if (Array.isArray(data.detail)) {
        return data.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
      }
      return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    }
    if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  }

  if (error.message) return error.message;
  return 'An unexpected error occurred';
};

export const userService = {
  getUsers: async (params?: any): Promise<{ users: User[], total?: number }> => {
    try {
      const response = await api.get<{ status: string, statuscode: number, users: User[], total?: number, count?: number, total_count?: number, user_count?: number }>(API_ENDPOINTS.getUsers(), { params });
      // Robust check for total count
      const total = response.data.user_count ?? response.data.total ?? response.data.count ?? response.data.total_count ?? 0;
      return { users: response.data.users, total };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  getReferrals: async (): Promise<User[]> => {
    try {
      const response = await api.get<{ status: string, statuscode: number, users: User[] }>(API_ENDPOINTS.getReferrals());
      return response.data.users;
    } catch (error) {
      console.error('Error fetching referrals:', error);
      throw error;
    }
  },

  getUserDetails: async (mobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getUserDetails(mobile));
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  },

  getNetwork: async (params?: any): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getReferrals(), { params });
      const data = response.data;
      // Ensure we pass back useful info if structure differs
      // If stats is missing but we have count/total, let's normalize it for the slice/component?
      // Actually, the slice expects { stats, users }. Layout usually matches. 
      // But if it's missing, let's try to polyfill in the slice or here.
      // Returning raw data is fine, slice handles interpretation.
      return data;
    } catch (error) {
      console.error('Error fetching network:', error);
      throw error;
    }
  },

  getNetworkUserDetails: async (mobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getReferrals() + `/${mobile}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching network user details:', error);
      throw error;
    }
  },

  createUser: async (userData: CreateUserRequest): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.createUser(), userData);

      // Check if response indicates an error despite 200 OK (soft error)
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: extractErrorMessage(response.data) };
      }

      return { data: response.data, message: 'User created successfully' };
    } catch (error: any) {
      console.error('Error creating user:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  verifyUser: async (mobile: string): Promise<void> => {
    try {
      const response = await api.post(API_ENDPOINTS.verifyUser(), { mobile, device_id: 'web', device_model: 'browser' });
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to verify user');
      }
    } catch (error) {
      console.error('Error verifying user:', error);
      throw error;
    }
  },

  updateUser: async (mobile: string, updates: Partial<User>): Promise<void> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateUser(mobile), updates);
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  checkHealth: async (): Promise<boolean> => {
    try {
      await api.get(API_ENDPOINTS.health());
      return true;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  },

  getAdmins: async (): Promise<any[]> => {
    try {
      const response = await api.get<{ status: string, statuscode: number, users: any[], count: number }>(API_ENDPOINTS.getAdmins());
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching admins:', error);
      throw error;
    }
  },

  addAdmin: async (adminData: any, superAdminMobile: string, superAdminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addAdmin(), adminData, {
        headers: {
          'x-admin-mobile': superAdminMobile,
          'x-admin-otp': superAdminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add admin' };
      }

      return { data: response.data, message: 'Admin added successfully' };
    } catch (error: any) {
      console.error('Error adding admin:', error);
      return { error: extractErrorMessage(error) };
    }
  }
};

export const farmService = {
  getFarms: async (status?: string): Promise<Farm[]> => {
    try {
      const params = status && status !== '--' ? { status } : {};
      const response = await api.get<Farm[]>(API_ENDPOINTS.getFarms(), { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching farms:', error);
      throw error;
    }
  },

  addFarm: async (farmData: CreateFarmRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addFarm(), farmData, {
        headers: {
          'X-Admin-Mobile': adminMobile,
          'x-admin-otp': adminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add farm' };
      }

      return { data: response.data, message: 'Farm added successfully' };
    } catch (error: any) {
      console.error('Error adding farm:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateFarm: async (farmId: string, farmData: CreateFarmRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateFarm(farmId), farmData, {
        headers: {
          'X-Admin-Mobile': adminMobile,
          'x-admin-otp': adminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update farm' };
      }

      return { data: response.data, message: 'Farm updated successfully' };
    } catch (error: any) {
      console.error('Error updating farm:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  assignAdminToFarm: async (farmId: string, adminMobile: string, superAdminMobile: string, superAdminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const payload: AssignAdminToFarmRequest = { admin_mobile: adminMobile };
      const response = await api.post(API_ENDPOINTS.assignAdminToFarm(farmId), payload, {
        headers: {
          'x-admin-mobile': superAdminMobile,
          'x-admin-otp': superAdminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to assign admin' };
      }

      return { data: response.data, message: 'Admin assigned successfully' };
    } catch (error: any) {
      console.error('Error assigning admin:', error);
      return { error: extractErrorMessage(error) };
    }
  }
};

export const otpService = {
  sendOtp: async (mobile: string, appName: string = 'TrueHarvest'): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.sendOtp(), { mobile, appName });
      return { data: response.data, message: 'OTP sent successfully' };
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      return { error: extractErrorMessage(error) };
    }
  }
};

export const selfBenefitService = {
  getSelfBenefits: async (farmId?: string): Promise<SelfBenefit[]> => {
    try {
      const params = farmId ? { farm_id: farmId } : {};
      const response = await api.get<SelfBenefit[]>(API_ENDPOINTS.getSelfBenefits(), { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching self benefits:', error);
      throw error;
    }
  },

  createSelfBenefit: async (benefitData: CreateSelfBenefitRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.getSelfBenefits(), benefitData, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to create benefit' };
      }

      return { data: response.data, message: 'Benefit created successfully' };
    } catch (error: any) {
      console.error('Error creating self benefit:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateSelfBenefit: async (benefitId: string, benefitData: CreateSelfBenefitRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      // payload no longer requires id in the body
      const { id, ...payload } = benefitData as any;
      const response = await api.put(API_ENDPOINTS.updateSelfBenefit(encodeURIComponent(benefitId)), payload, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp,
          'id': benefitId
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update benefit' };
      }

      return { data: response.data, message: 'Benefit updated successfully' };
    } catch (error: any) {
      console.error('Error updating self benefit:', error);
      return { error: extractErrorMessage(error) };
    }
  },


};

export const referralBenefitService = {
  getReferralMilestones: async (farmId?: string): Promise<ReferralMilestone[]> => {
    try {
      const params = farmId ? { farm_id: farmId } : {};
      const response = await api.get<ReferralMilestone[]>(API_ENDPOINTS.getReferralMilestones(), { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching referral milestones:', error);
      throw error;
    }
  },
  createReferralMilestone: async (data: CreateReferralMilestoneRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<ReferralMilestone>> => {
    try {
      const response = await api.post<ApiResponse<ReferralMilestone>>(API_ENDPOINTS.getReferralMilestones(), data, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating referral milestone:', error);
      throw error;
    }
  },
  updateReferralMilestone: async (id: string, data: CreateReferralMilestoneRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<ReferralMilestone>> => {
    try {
      const response = await api.put<ApiResponse<ReferralMilestone>>(API_ENDPOINTS.updateReferralMilestone(id), data, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating referral milestone:', error);
      throw error;
    }
  },
  getAchievedReferralMilestones: async (adminMobile: string): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.getAchievedReferralMilestones!(), {
        headers: {
          'x-admin-mobile': adminMobile
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching achieved referral milestones:', error);
      throw error;
    }
  }
};

export const referralConfigService = {
  getReferralConfig: async (farmId?: string): Promise<ReferralConfig> => {
    try {
      const params = farmId ? { farm_id: farmId } : {};
      const response = await api.get<ReferralConfig>(API_ENDPOINTS.getReferralConfig(), { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching referral config:', error);
      throw error;
    }
  },
  updateReferralConfig: async (data: UpdateReferralConfigRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<ReferralConfig>> => {
    try {
      const response = await api.put<ApiResponse<ReferralConfig>>(API_ENDPOINTS.updateReferralConfig(), data, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating referral config:', error);
      throw error;
    }
  }
};

export const roleRequestService = {
  getRoleChangeRequests: async (statusFilter: string = 'ALL', adminMobile: string): Promise<RoleChangeRequest[]> => {
    try {
      const params = {
        status_filter: statusFilter,
        admin_mobile: adminMobile
      };
      const response = await api.get<RoleChangeRequestResponse>(API_ENDPOINTS.getRoleChangeRequests(), { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching role change requests:', error);
      throw error;
    }
  },
  actionRoleChangeRequest: async (requestId: string, approved: boolean, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.actionRoleChangeRequest(requestId), { approved }, {
        params: { admin_mobile: adminMobile },
        headers: {
          'X-Admin-Mobile': adminMobile,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      return { data: response.data, message: `Request ${approved ? 'approved' : 'rejected'} successfully` };
    } catch (error: any) {
      console.error(`Error ${approved ? 'approving' : 'rejecting'} role request:`, error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || `Failed to ${approved ? 'approve' : 'reject'} request`;
      return { error: errorMessage };
    }
  }
};

export const achievedBenefitService = {
  getAchievedBenefits: async (adminMobile: string): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.getAchievedBenefits(), {
        headers: {
          'x-admin-mobile': adminMobile
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching achieved benefits:', error);
      throw error;
    }
  }
};

export const orderService = {
  getOrderDetails: async (orderId: string): Promise<any> => {

    try {
      const response = await api.get<any>(API_ENDPOINTS.getOrderDetails(orderId));
      return response.data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  }
};

export const procurementService = {
  getProcurementRoster: async (params: { stage: string; date?: string; page?: number; page_size?: number }): Promise<{ data: any[], totalGroups?: number }> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getProcurementRoster(), { params });
      const totalGroups = response.data?.totalGroups ?? response.data?.total_groups;

      // Case 1: Grouped data (usually Basket stages)
      if (response.data && Array.isArray(response.data.data)) {
        return { data: response.data.data, totalGroups: totalGroups ?? response.data.data.length };
      }

      // Case 2: Flat list (usually Procurement/Tracking stages)
      if (response.data && Array.isArray(response.data.buffaloes)) {
        const flatData = [{
          approvedDate: response.data.filterDate || null,
          totalBuffaloes: response.data.totalBuffaloes || response.data.buffaloes.length,
          buffaloes: response.data.buffaloes
        }];
        return { data: flatData, totalGroups: totalGroups ?? 1 };
      }

      return { data: [], totalGroups: 0 };
    } catch (error) {
      console.error('Error fetching procurement roster:', error);
      throw error;
    }
  },

  performProcurementAction: async (data: {
    action: string;
    buffaloIds: string[];
    marketName?: string;
    marketId?: string;
    sendNotification: boolean;
    adminMobile: string;
  }): Promise<any> => {
    try {
      const { adminMobile, ...payload } = data;
      const response = await api.post<any>(API_ENDPOINTS.procurementAction(), payload, {
        headers: {
          'x-admin-mobile': adminMobile || 'true'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error performing procurement action:', error);
      throw error;
    }
  }
};

export const marketService = {
  getMarkets: async (): Promise<Market[]> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getMarkets());
      // Handle both direct array and wrapped object responses
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.markets)) {
        return response.data.markets;
      }
      return [];
    } catch (error) {
      console.error('Error fetching markets:', error);
      throw error;
    }
  },

  addMarket: async (marketData: CreateMarketRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addMarket(), marketData, {
        headers: {
          'x-admin-mobile': adminMobile,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add market' };
      }

      return { data: response.data, message: 'Market added successfully' };
    } catch (error: any) {
      console.error('Error adding market:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateMarket: async (marketId: string, marketData: CreateMarketRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateMarket(marketId), marketData, {
        headers: {
          'x-admin-mobile': adminMobile,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update market' };
      }

      return { data: response.data, message: 'Market updated successfully' };
    } catch (error: any) {
      console.error('Error updating market:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  toggleMarketStatus: async (marketId: string, isActive: boolean, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.patch(API_ENDPOINTS.toggleMarketStatus(marketId), { isActive }, {
        headers: {
          'x-admin-mobile': adminMobile,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to toggle status' };
      }

      return { data: response.data, message: `Market ${isActive ? 'activated' : 'deactivated'} successfully` };
    } catch (error: any) {
      console.error('Error toggling market status:', error);
      return { error: extractErrorMessage(error) };
    }
  }
};
